import Vue from "vue";
import {v4 as uuidv4} from "uuid";
import Repository from "@/repository";

const CHAT_API = "/chat";
const PAGE = 50;

// Chat between people in the account. The server stores everything; live
// updates arrive over MQTT on user/<account>/<contact>/chat and come in
// through the receive action (see MqttHelper).

// ----------------------- STATE ------------------------- //
const state = {
    conversations: [],
    contacts: [],
    // tracked vehicles that can be asked about, through the AI
    vehicles: [],
    // conversation id -> true while the AI is writing an answer there
    typing: {},
    // conversation id -> messages, oldest first
    messages: {},
    // conversation id -> true once the oldest message has been loaded
    complete: {},
    currentId: null,
    loaded: false,
    // how many chat views (the Messenger page, the drawer) are on screen
    viewers: 0,
    // what the role allows: {can_start, can_announce}
    access: {can_start: true, can_announce: false},
};

const myContactId = rootState => rootState.profile.userInfo.contact ? rootState.profile.userInfo.contact.id : null;

const fullName = person => `${person.first_name || ""} ${person.last_name || ""}`.trim();

// ----------------------- GETTERS ------------------------- //
const getters = {
    me: (state, getters, rootState) => myContactId(rootState),

    current: state => state.conversations.find(c => c.id === state.currentId) || null,

    currentMessages: state => state.messages[state.currentId] || [],

    // a group's title, or the other person's name
    titleOf: (state, getters) => conversation => {
        if (!conversation) return "";
        if (conversation.kind === "group") return conversation.title || "Group";
        if (conversation.kind === "announcement") return conversation.title || "Announcement";
        if (conversation.kind === "vehicle") return conversation.title || (conversation.vehicle && conversation.vehicle.name) || "Vehicle";
        const other = (conversation.members || []).find(m => m.contact_id !== getters.me);
        return other ? fullName(other) : "Just me";
    },

    nameOf: state => contactId => {
        for (const conversation of state.conversations) {
            const member = (conversation.members || []).find(m => m.contact_id === contactId);
            if (member) return fullName(member);
        }
        const contact = state.contacts.find(c => c.id === contactId);
        return contact ? fullName(contact) : "";
    },

    vehicleName: (state, getters, rootState) => vehicleId => {
        const vehicle = (rootState.vehicles.all || []).find(v => v.id === vehicleId);
        return vehicle ? (vehicle.name || vehicle.number_plate || "") : "";
    },

    totalUnread: state => state.conversations.reduce((sum, c) => sum + (c.unread || 0), 0),

    // the members other than the sender who have read up to this message, and those who haven't
    readersOf: (state) => message => {
        const conversation = state.conversations.find(c => c.id === message.conversation_id);
        const others = conversation ? (conversation.members || []).filter(m => m.contact_id !== message.sender_contact_id) : [];
        const read = others.filter(m => m.last_read_at && new Date(m.last_read_at) >= new Date(message.created_at));
        return {read, unread: others.filter(m => !read.includes(m))};
    },

    // whether everyone else in the conversation has read up to this message
    isReadByOthers: (state, getters) => message => {
        const conversation = state.conversations.find(c => c.id === message.conversation_id);
        if (!conversation) return false;
        const others = (conversation.members || []).filter(m => m.contact_id !== getters.me);
        return others.length > 0 && others.every(m => m.last_read_at && new Date(m.last_read_at) >= new Date(message.created_at));
    },
};

// ----------------------- MUTATIONS ------------------------- //
const sortConversations = state => {
    const at = c => new Date(c.last_message_at || c.created_at).getTime();
    state.conversations.sort((a, b) => at(b) - at(a));
};

const mutations = {
    setConversations(state, conversations) {
        state.conversations = conversations || [];
        sortConversations(state);
        state.loaded = true;
    },

    setAccess(state, access) {
        state.access = access;
    },

    setContacts(state, contacts) {
        state.contacts = contacts || [];
    },

    setVehicles(state, vehicles) {
        state.vehicles = vehicles || [];
    },

    setTyping(state, {conversationId, typing}) {
        if (typing) {
            Vue.set(state.typing, conversationId, true);
        } else {
            Vue.delete(state.typing, conversationId);
        }
    },

    upsertConversation(state, conversation) {
        const index = state.conversations.findIndex(c => c.id === conversation.id);
        if (index > -1) {
            state.conversations.splice(index, 1, {...state.conversations[index], ...conversation});
        } else {
            state.conversations.push(conversation);
        }
        sortConversations(state);
    },

    setCurrent(state, id) {
        state.currentId = id;
    },

    // +1 when a chat view comes on screen, -1 when it leaves
    changeViewers(state, delta) {
        state.viewers = Math.max(0, state.viewers + delta);
    },

    // older is a page loaded before what we have
    setMessages(state, {id, messages, older}) {
        const have = state.messages[id] || [];
        const known = new Set(have.map(m => m.id));
        const fresh = messages.filter(m => !known.has(m.id));
        Vue.set(state.messages, id, older ? [...fresh, ...have] : [...have, ...fresh]);
        if (older || have.length === 0) {
            Vue.set(state.complete, id, messages.length < PAGE);
        }
    },

    // a new message, or the server's copy of one we sent (same client_msg_id)
    addMessage(state, message) {
        const list = state.messages[message.conversation_id];
        if (!list) {
            Vue.set(state.messages, message.conversation_id, [message]);
            return;
        }
        const index = list.findIndex(m => m.id === message.id ||
            (message.client_msg_id && m.client_msg_id === message.client_msg_id));
        if (index > -1) {
            list.splice(index, 1, message);
        } else {
            list.push(message);
        }
    },

    setMessageStatus(state, {conversationId, clientMsgId, status}) {
        const list = state.messages[conversationId] || [];
        const message = list.find(m => m.client_msg_id === clientMsgId);
        if (message) Vue.set(message, "status", status);
    },

    // newest message shown in the list, and the unread count
    touchConversation(state, {message, unread}) {
        const conversation = state.conversations.find(c => c.id === message.conversation_id);
        if (!conversation) return;
        conversation.last_message = message;
        conversation.last_message_at = message.created_at;
        if (unread) conversation.unread = (conversation.unread || 0) + 1;
        sortConversations(state);
    },

    setRead(state, {conversationId, contactId, readAt}) {
        const conversation = state.conversations.find(c => c.id === conversationId);
        if (!conversation) return;
        const member = (conversation.members || []).find(m => m.contact_id === contactId);
        if (member) member.last_read_at = readAt;
    },

    clearUnread(state, conversationId) {
        const conversation = state.conversations.find(c => c.id === conversationId);
        if (conversation) conversation.unread = 0;
    },

    reset(state) {
        state.conversations = [];
        state.contacts = [];
        state.vehicles = [];
        state.typing = {};
        state.messages = {};
        state.complete = {};
        state.currentId = null;
        state.loaded = false;
        state.viewers = 0;
    },
};

// ----------------------- ACTIONS ------------------------- //
const actions = {
    async loadConversations({commit}) {
        commit("setConversations", (await Repository.get(`${CHAT_API}/conversations`)).data);
    },

    async loadAccess({commit}) {
        commit("setAccess", (await Repository.get(`${CHAT_API}/me`)).data);
    },

    async createAnnouncement({commit, dispatch}, {title, contactIds}) {
        const conversation = (await Repository.post(`${CHAT_API}/conversations/announcement`, {title, contact_ids: contactIds})).data;
        commit("upsertConversation", conversation);
        await dispatch("openConversation", conversation.id);
        return conversation;
    },

    async loadContacts({commit}) {
        commit("setContacts", (await Repository.get(`${CHAT_API}/contacts`)).data);
    },

    async openConversation({commit, dispatch, state}, id) {
        commit("setCurrent", id);
        if (!state.messages[id]) {
            await dispatch("loadMessages", {id});
        }
        dispatch("markRead", id);
    },

    async loadMessages({commit}, {id, before}) {
        const params = {limit: PAGE};
        if (before) params.before = before;
        const messages = (await Repository.get(`${CHAT_API}/conversations/${id}/messages`, {params})).data;
        commit("setMessages", {id, messages, older: !!before});
    },

    async loadOlder({dispatch, state}, id) {
        const list = state.messages[id] || [];
        if (list.length === 0 || state.complete[id]) return;
        await dispatch("loadMessages", {id, before: list[0].created_at});
    },

    async loadVehicles({commit}) {
        commit("setVehicles", (await Repository.get(`${CHAT_API}/vehicles`)).data);
    },

    // my chat with a vehicle, where the AI answers
    async openVehicleChat({commit, dispatch}, vehicleId) {
        const conversation = (await Repository.post(`${CHAT_API}/conversations/vehicle-chat`, {vehicle_id: vehicleId})).data;
        commit("upsertConversation", conversation);
        await dispatch("openConversation", conversation.id);
        return conversation;
    },

    async openDirect({commit, dispatch}, contactId) {
        const conversation = (await Repository.post(`${CHAT_API}/conversations/direct`, {contact_id: contactId})).data;
        commit("upsertConversation", conversation);
        await dispatch("openConversation", conversation.id);
        return conversation;
    },

    async createGroup({commit, dispatch}, {title, contactIds}) {
        const conversation = (await Repository.post(`${CHAT_API}/conversations/group`, {title, contact_ids: contactIds})).data;
        commit("upsertConversation", conversation);
        await dispatch("openConversation", conversation.id);
        return conversation;
    },

    // shows the message straight away as "sending", then swaps in the server's copy
    async send({commit, dispatch, getters}, {conversationId, body, clientMsgId, attachment, urgent}) {
        const text = (body || "").trim();
        if (!text && !attachment) return;
        const key = clientMsgId || uuidv4();
        if (!clientMsgId) {
            const pending = {
                id: `pending-${key}`,
                client_msg_id: key,
                conversation_id: conversationId,
                sender_contact_id: getters.me,
                kind: attachment ? "attachment" : "text",
                body: text,
                attachment_id: attachment ? attachment.id : null,
                attachment: attachment || null,
                urgent: !!urgent,
                created_at: new Date().toISOString(),
                status: "sending",
            };
            commit("addMessage", pending);
            commit("touchConversation", {message: pending});
        } else {
            commit("setMessageStatus", {conversationId, clientMsgId: key, status: "sending"});
        }
        try {
            const message = (await Repository.post(`${CHAT_API}/conversations/${conversationId}/messages`, {
                body: text,
                client_msg_id: key,
                attachment_id: attachment ? attachment.id : undefined,
                urgent: !!urgent,
            })).data;
            commit("addMessage", message);
            commit("touchConversation", {message});
        } catch (e) {
            commit("setMessageStatus", {conversationId, clientMsgId: key, status: "failed"});
            dispatch("noteFailure", e);
        }
    },

    retry({dispatch}, message) {
        return dispatch("send", {conversationId: message.conversation_id, body: message.body, clientMsgId: message.client_msg_id, attachment: message.attachment, urgent: message.urgent});
    },

    // uploads a file as a blob; send it with send({attachment})
    async upload(context, {file, onProgress}) {
        const form = new FormData();
        form.append("file", file);
        form.append("name", file.name);
        form.append("type", "chat");
        form.append("description", "");
        return (await Repository.post(`/upload/blob`, form, {
            headers: {"Content-Type": "multipart/form-data"},
            onUploadProgress: onProgress,
        })).data;
    },

    openDrawer({commit}) {
        commit("showRightDrawer", {title: "Messages", width: 420, component: "ChatDrawer"}, {root: true});
    },

    // opens the chat with whoever drives the vehicle now
    async messageDriver({commit, dispatch}, vehicleId) {
        try {
            const conversation = (await Repository.post(`${CHAT_API}/conversations/vehicle`, {vehicle_id: vehicleId})).data;
            commit("upsertConversation", conversation);
            await dispatch("openConversation", conversation.id);
            dispatch("openDrawer");
        } catch (e) {
            dispatch("noteFailure", e);
        }
    },

    // the vehicle's own chat, where the AI answers questions about it
    async askVehicleAI({dispatch}, vehicleId) {
        try {
            await dispatch("openVehicleChat", vehicleId);
            dispatch("openDrawer");
        } catch (e) {
            dispatch("noteFailure", e);
        }
    },

    // after the live connection comes back: refresh the list and the open thread
    async resync({state, dispatch}) {
        if (!state.loaded) return;
        try {
            await dispatch("loadConversations");
            if (state.currentId && state.messages[state.currentId]) {
                await dispatch("loadMessages", {id: state.currentId});
            }
        } catch (e) {
            // the next reconnect tries again
        }
    },

    async markRead({commit}, id) {
        commit("clearUnread", id);
        try {
            await Repository.post(`${CHAT_API}/conversations/${id}/read`, {});
        } catch (e) {
            // the next open marks it again
        }
    },

    noteFailure({commit}, e) {
        const reason = e && e.response && typeof e.response.data === "string" ? e.response.data.trim() : "Message not sent";
        commit("showSnackBar", {color: "error", message: reason}, {root: true});
    },

    // an event from MQTT
    async receive({commit, dispatch, state, getters}, event) {
        const visible = state.viewers > 0 && !document.hidden;
        switch (event.type) {
            case "message": {
                const message = event.message;
                if (!state.conversations.find(c => c.id === message.conversation_id)) {
                    // someone started a conversation with me
                    try {
                        commit("upsertConversation", (await Repository.get(`${CHAT_API}/conversations/${message.conversation_id}`)).data);
                    } catch (e) {
                        return;
                    }
                }
                // the AI's answers carry the asker as sender but come from the vehicle
                const mine = message.sender_contact_id === getters.me && message.kind !== "ai" && message.kind !== "alert";
                if (message.kind === "ai") {
                    commit("setTyping", {conversationId: message.conversation_id, typing: false});
                }
                const reading = visible && state.currentId === message.conversation_id;
                if (state.messages[message.conversation_id]) {
                    commit("addMessage", message);
                }
                commit("touchConversation", {message, unread: !mine && !reading});
                if (!mine && reading) {
                    dispatch("markRead", message.conversation_id);
                }
                break;
            }
            case "typing":
                if (event.conversation_id) {
                    const id = event.conversation_id;
                    commit("setTyping", {conversationId: id, typing: true});
                    // never spin forever if the answer is lost
                    setTimeout(() => commit("setTyping", {conversationId: id, typing: false}), 120000);
                }
                break;
            case "read":
                commit("setRead", {conversationId: event.conversation_id, contactId: event.contact_id, readAt: event.read_at});
                if (event.contact_id === getters.me) {
                    commit("clearUnread", event.conversation_id);
                }
                break;
            case "conversation":
                if (event.conversation) {
                    // the creator's view; my unread count starts at nothing read
                    commit("upsertConversation", {...event.conversation, unread: 0});
                }
                break;
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
